import { backofficeFetch } from '@/services/backoffice-api';
import type { BackofficeDashboardResponse } from './types';

const DASHBOARD_CACHE_TTL_MS = 60_000;
const DASHBOARD_CACHE_KEY_PREFIX = 'backoffice:dashboard:';

interface DashboardCacheEntry {
  expiresAt: number;
  response: BackofficeDashboardResponse;
}

export interface FetchBackofficeDashboardOptions {
  forceRefresh?: boolean;
}

function getCacheKey(from: string, to: string): string {
  return `${DASHBOARD_CACHE_KEY_PREFIX}${from}:${to}`;
}

function readCachedDashboard(from: string, to: string): BackofficeDashboardResponse | null {
  try {
    const serialized = sessionStorage.getItem(getCacheKey(from, to));
    if (!serialized) {
      return null;
    }

    const entry = JSON.parse(serialized) as DashboardCacheEntry;
    if (entry.expiresAt <= Date.now()) {
      sessionStorage.removeItem(getCacheKey(from, to));
      return null;
    }

    return entry.response;
  } catch {
    return null;
  }
}

function cacheDashboard(from: string, to: string, response: BackofficeDashboardResponse): void {
  try {
    const entry: DashboardCacheEntry = {
      expiresAt: Date.now() + DASHBOARD_CACHE_TTL_MS,
      response,
    };
    sessionStorage.setItem(getCacheKey(from, to), JSON.stringify(entry));
  } catch {
    // The dashboard remains functional when browser storage is unavailable.
  }
}

export async function fetchBackofficeDashboard(
  from: string,
  to: string,
  signal?: AbortSignal,
  { forceRefresh = false }: FetchBackofficeDashboardOptions = {}
): Promise<BackofficeDashboardResponse> {
  if (!forceRefresh) {
    const cached = readCachedDashboard(from, to);
    if (cached) {
      return cached;
    }
  }

  const params = new URLSearchParams({ from, to });
  const response = await backofficeFetch(`/dashboard?${params.toString()}`, {
    headers: { Accept: 'application/json' },
    ...(forceRefresh ? { cache: 'no-store' } : {}),
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) throw new Error(`Failed to load dashboard (${response.status})`);
  const payload = (await response.json()) as BackofficeDashboardResponse;
  cacheDashboard(from, to, payload);
  return payload;
}
